/**
 * Convert SRT (or SRT-like) text into the numbered cue format expected by
 * extension `parseWebVtt` (starts scanning at line "1").
 */
export function srtToWebVtt(srt: string): string {
  const normalized = srt.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) return '';

  // Already numbered with --> timings: pass through (optionally strip WEBVTT header).
  const withoutHeader = normalized.replace(/^WEBVTT[^\n]*\n+/i, '');
  if (/^1\s*\n/m.test(withoutHeader) || withoutHeader.startsWith('1\n')) {
    return withoutHeader.trim() + '\n';
  }

  const blocks = withoutHeader.split(/\n\s*\n/);
  const out: string[] = [];
  let index = 1;

  for (const block of blocks) {
    const lines = block
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) continue;

    const timingIdx = lines.findIndex((l) => /-->/.test(l));
    if (timingIdx === -1) continue;

    // Drop a leading cue number if present (timing stays at index 1).
    const timingLine = (lines[timingIdx] ?? '').replace(/,/g, '.');
    const textLines = lines.slice(timingIdx + 1);
    if (textLines.length === 0) continue;

    out.push(String(index));
    out.push(timingLine);
    out.push(...textLines);
    out.push('');
    index += 1;
  }

  return out.join('\n');
}

/** Detect whether a json3 document looks like ASR (word-level tOffsetMs). */
export function isJson3Asr(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const events = (raw as { events?: unknown }).events;
  if (!Array.isArray(events)) return false;
  for (const ev of events) {
    if (!ev || typeof ev !== 'object') continue;
    const segs = (ev as { segs?: unknown }).segs;
    if (!Array.isArray(segs)) continue;
    for (const seg of segs) {
      if (seg && typeof seg === 'object' && 'tOffsetMs' in seg) return true;
    }
  }
  return false;
}
